import { createRouter, createRoute, createRootRoute, redirect, Outlet, lazyRouteComponent } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { DOCS_DOMAIN } from '_/common/common.constants';
import AlarmGroupsContainer from '_/components/AlarmGroups/AlarmGroupsContainer';
import SelectedAlarmGroup from '_/components/AlarmGroups/SelectedAlarmGroup';
import AlarmsContainer from '_/components/Alarms/AlarmsContainer';
import SelectedAlarm from '_/components/Alarms/SelectedAlarm';
import App from '_/components/App';
import CollectionPushes from '_/components/Collections/CollectionPushes';
import CollectionsContainer from '_/components/Collections/CollectionsContainer';
import Forbidden from '_/components/common/Forbidden';
import NotFound from '_/components/common/NotFound';
import DeliveriesContainer from '_/components/Deliveries/DeliveriesContainer';
import OrganizationDeliveries from '_/components/Deliveries/OrganizationDeliveries';
import EditDestination from '_/components/Destinations/DestinationForms/EditDestination';
import NewDestination from '_/components/Destinations/DestinationForms/NewDestination';
import DestinationsContainer from '_/components/Destinations/DestinationsContainer';
import OrganizationDestinations from '_/components/Destinations/OrganizationDestinations';
import ExtractionCellsContainer from '_/components/ExtractionCells/ExtractionCellsContainer';
import ExtractorsContainer from '_/components/Extractors/ExtractorsContainer';
import SelectedIntegration from '_/components/Integrations/SelectedIntegration';
import LoginPoolsContainer from '_/components/LoginPools/LoginPoolsContainer';
import SelectedLoginPool from '_/components/LoginPools/SelectedLoginPool';
import LoginsContainer from '_/components/Logins/LoginsContainer';
import SelectedLogin from '_/components/Logins/SelectedLogin';
import OrgSettings from '_/components/Organizations/OrgSettings';
import SelectedOrg from '_/components/Organizations/SelectedOrg';
import FixQueue from '_/components/Projects/FixQueue';
import MyFixing from '_/components/Projects/MyFixing';
import MyQA from '_/components/Projects/MyQA';
import ProjectReadmeHistory from '_/components/Projects/ProjectReadmeHistory';
import QaQueue from '_/components/Projects/QaQueue';
import OrganizationSchemas from '_/components/Schemas/OrganizationSchemas';
import SchemasContainer from '_/components/Schemas/SchemasContainer';
import SnapshotCompare from '_/components/Snapshots/SnapshotCompare';
import SnapshotDrilldown from '_/components/Snapshots/SnapshotDrilldown';
import SnapshotGraphs from '_/components/Snapshots/SnapshotGraphs';
import SnapshotsContainer from '_/components/Snapshots/SnapshotsContainer';
import SourceGraphs from '_/components/Sources/SourceGraphs';
import SourceReadmeHistory from '_/components/Sources/SourceReadmeHistory';
import SourcesContainer from '_/components/Sources/SourcesContainer';
import SourceSettings from '_/components/Sources/SourceSettings';
import FlowPage from '_/features/flows/flowPage/components/FlowPage/FlowPage';
import NewFlowContainer from '_/features/flows/flowPage/components/NewFlowContainer/NewFlowContainer';
import FlowList from '_/features/flows/list/components/FlowList/FlowList';
import Login from '_/features/users/Login';
import React from 'react';

// Larger components (> 2KB gzipped) - lazy loaded to keep initial bundle small
// These will be loaded on-demand when the user navigates to their routes

// ============================================================================
// ROOT ROUTE
// ============================================================================

const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      {process.env.NODE_ENV === 'development' && <TanStackRouterDevtools />}
    </>
  ),
});

// ============================================================================
// EXTERNAL REDIRECTS
// ============================================================================

const apiDocsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/api/docs',
  beforeLoad: () => {
    window.location.href = 'https://workbench.import.io/api/docs';
    throw redirect({ to: '/' });
  },
  component: () => null,
});

const docsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs',
  beforeLoad: () => {
    window.location.href = DOCS_DOMAIN;
    throw redirect({ to: '/' });
  },
  component: () => null,
});

// ============================================================================
// LOGIN ROUTE
// ============================================================================

const userLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: Login,
});

// ============================================================================
// APP ROOT (Authenticated Routes)
// ============================================================================

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: App,
});

// ============================================================================
// TOP-LEVEL ROUTES
// ============================================================================

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/orgs' });
  },
});

// Organizations
const orgsIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/orgs',
  component: lazyRouteComponent(() => import('_/components/Organizations/OrganizationsContainer')),
});

// System Users
const systemUsersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/systemusers',
  component: lazyRouteComponent(() => import('_/components/Users/SystemUsersContainer')),
});

// User Account
const userAccountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/users',
  component: lazyRouteComponent(() => import('_/components/Users/UserAccount')),
});

// Recent Snapshots (super admin)
const recentSnapshotsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/recentsnapshots',
  component: lazyRouteComponent(() => import('_/components/Snapshots/RecentSnapshotsContainer')),
});

// Extraction Cells
const extractionCellsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/extractioncells',
  component: ExtractionCellsContainer,
});

const extractionCellsIndexRoute = createRoute({
  getParentRoute: () => extractionCellsRoute,
  path: '/',
  component: lazyRouteComponent(() => import('_/components/ExtractionCells/SystemExtractionCells')),
});

const extractionCellsNewRoute = createRoute({
  getParentRoute: () => extractionCellsRoute,
  path: '/_new',
  component: lazyRouteComponent(() => import('_/components/ExtractionCells/NewSystemExtractionCell')),
});

const extractionCellsEditRoute = createRoute({
  getParentRoute: () => extractionCellsRoute,
  path: '/$extractionCellSlug/edit',
  component: lazyRouteComponent(() => import('_/components/ExtractionCells/NewSystemExtractionCell')),
});

// Forbidden & Not Found
const forbiddenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/forbidden',
  component: Forbidden,
});

const notFoundRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '*',
  component: NotFound,
});

// ============================================================================
// ORGANIZATION ROUTES
// ============================================================================

const orgRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/orgs/$orgSlug',
  component: SelectedOrg,
});

// Org Home & Settings
const orgIndexRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/orgs/$orgSlug/home', params: params });
  },
});

const orgHomeRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/home',
  component: lazyRouteComponent(() => import('_/components/Organizations/OrgHome')),
});

const orgReadmeHistoryRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/readmeversions',
  component: lazyRouteComponent(() => import('_/components/Organizations/OrgReadmeHistory')),
});

const orgSettingsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/settings',
  component: OrgSettings,
});

const orgPeopleRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/people',
  component: lazyRouteComponent(() => import('_/components/Organizations/OrgPeople')),
});

const orgIntegrationsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/integrations',
  component: lazyRouteComponent(() => import('_/components/Integrations/Integrations')),
});

const selectedIntegrationRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/integrations/$integrationId',
  component: SelectedIntegration,
});

const editIntegrationRoute = createRoute({
  getParentRoute: () => selectedIntegrationRoute,
  path: '/edit',
  component: lazyRouteComponent(() => import('_/components/Integrations/NewIntegration')),
});

// Organization Deliveries
const orgDeliveriesRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/deliveries',
  component: DeliveriesContainer,
});

const orgDeliveriesIndexRoute = createRoute({
  getParentRoute: () => orgDeliveriesRoute,
  path: '/',
  component: OrganizationDeliveries,
});

const orgDeliveryRoute = createRoute({
  getParentRoute: () => orgDeliveriesRoute,
  path: '/$deliveryId',
  component: lazyRouteComponent(() => import('_/components/Deliveries/SelectedDelivery')),
});

const orgDeliveryIndexRoute = createRoute({
  getParentRoute: () => orgDeliveryRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/orgs/$orgSlug/deliveries/$deliveryId/home', params: params });
  },
});

const orgDeliveryHomeRoute = createRoute({
  getParentRoute: () => orgDeliveryRoute,
  path: '/home',
  component: lazyRouteComponent(() => import('_/components/Deliveries/DeliveryHome')),
});

// Destinations
const destinationsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/destinations',
  component: DestinationsContainer,
});

const destinationsIndexRoute = createRoute({
  getParentRoute: () => destinationsRoute,
  path: '/',
  component: OrganizationDestinations,
});

const newDestinationRoute = createRoute({
  getParentRoute: () => destinationsRoute,
  path: '/_new',
  component: NewDestination,
});

const editDestinationRoute = createRoute({
  getParentRoute: () => destinationsRoute,
  path: '/$destinationId/edit',
  component: EditDestination,
});

// Schemas
const schemasRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/schemas',
  component: SchemasContainer,
});

const schemasIndexRoute = createRoute({
  getParentRoute: () => schemasRoute,
  path: '/',
  component: OrganizationSchemas,
});

const schemaNewRoute = createRoute({
  getParentRoute: () => schemasRoute,
  path: '/_new',
  component: lazyRouteComponent(() => import('_/components/Schemas/NewSchema/NewSchema')),
});

const schemaRoute = createRoute({
  getParentRoute: () => schemasRoute,
  path: '/$schemaSlug',
  component: lazyRouteComponent(() => import('_/components/Schemas/SelectedSchema/SelectedSchema')),
});

const schemaEditRoute = createRoute({
  getParentRoute: () => schemasRoute,
  path: '/$schemaSlug/edit',
  component: lazyRouteComponent(() => import('_/components/Schemas/NewSchema/NewSchema')),
});

// Extractors
const extractorsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/extractors',
  component: ExtractorsContainer,
});

const extractorsIndexRoute = createRoute({
  getParentRoute: () => extractorsRoute,
  path: '/',
  component: lazyRouteComponent(() => import('_/components/Extractors/OrganizationExtractors')),
});

// Snapshot Shortcut
const snapshotShortcutRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/snapshots/$snapshotId',
  component: lazyRouteComponent(() => import('_/components/Snapshots/SnapshotShortcutResolver')),
});

// ============================================================================
// PROJECT ROUTES
// ============================================================================

const projectsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/projects',
  component: lazyRouteComponent(() => import('_/components/Projects/ProjectsContainer')),
});

const projectsNewRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/projects/_new',
  component: lazyRouteComponent(() => import('_/components/Projects/ProjectForms/NewProject')),
});

const projectRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/projects/$projectSlug',
  component: lazyRouteComponent(() => import('_/features/projects/components/SelectedProject')),
});

const projectIndexRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/orgs/$orgSlug/projects/$projectSlug/home', params: params });
  },
});

const projectHomeRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/home',
  component: lazyRouteComponent(() => import('_/components/Projects/ProjectHome')),
});

const projectEditRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/edit',
  component: lazyRouteComponent(() => import('_/components/Projects/ProjectForms/EditProject')),
});

const projectReadmeHistoryRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/readmeversions',
  component: ProjectReadmeHistory,
});

const projectCellRoutesRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/cellroutes',
  component: lazyRouteComponent(() => import('_/components/Projects/ProjectCellRoutes')),
});

const projectCellMetricsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/cellmetrics',
  component: lazyRouteComponent(() => import('_/components/Projects/ProjectCellMetrics')),
});

const projectQaQueueRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/qaqueue',
  component: QaQueue,
});

const projectFixQueueRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/fixqueue',
  component: FixQueue,
});

const projectMyQARoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/myqa',
  component: MyQA,
});

const projectMyFixingRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/myfixing',
  component: MyFixing,
});

// ============================================================================
// FLOW ROUTES
// ============================================================================

const flowsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/flows',
  component: FlowList,
});

const flowsNewRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/flows/_new',
  component: NewFlowContainer,
});

const flowRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/flows/$flowSlug',
  component: FlowPage,
});

const flowIndexRoute = createRoute({
  getParentRoute: () => flowRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/orgs/$orgSlug/projects/$projectSlug/flows/$flowSlug/home',
      params: params,
    });
  },
});

const flowHomeRoute = createRoute({
  getParentRoute: () => flowRoute,
  path: '/home',
  component: lazyRouteComponent(() => import('_/features/flows/flowPage/components/FlowHome/FlowHome')),
});

const flowEditRoute = createRoute({
  getParentRoute: () => flowRoute,
  path: '/edit',
  component: lazyRouteComponent(() => import('_/features/flows/flowPage/components/EditFlowContainer/EditFlowContainer')),
});

const flowReadmeHistoryRoute = createRoute({
  getParentRoute: () => flowRoute,
  path: '/readmeversions',
  component: lazyRouteComponent(() => import('_/features/flows/flowPage/components/FlowReadmeHistory/FlowReadmeHistory')),
});

const flowDeliveriesRoute = createRoute({
  getParentRoute: () => flowRoute,
  path: '/deliveries',
  component: lazyRouteComponent(() => import('_/features/flowDeliveries/components/FlowDeliveries')),
});

const flowDestinationsRoute = createRoute({
  getParentRoute: () => flowRoute,
  path: '/destinations',
  component: lazyRouteComponent(() => import('_/features/flowDestinations/components/FlowDestinations/FlowDestinationsPage')),
});

const flowSettingsRoute = createRoute({
  getParentRoute: () => flowRoute,
  path: '/settings',
  component: lazyRouteComponent(() => import('_/features/flowSettings/components/FlowSettingsPage')),
});

const flowSegmentSettingsRoute = createRoute({
  getParentRoute: () => flowRoute,
  path: '/settings/$flowSegmentId/settings',
  component: lazyRouteComponent(() => import('_/features/flowSettings/components/FlowSegmentSettings')),
});

const flowRunRoute = createRoute({
  getParentRoute: () => flowRoute,
  path: '/run/$step',
  component: lazyRouteComponent(() => import('_/features/flows/flowPage/components/FlowRun/FlowRun')),
});

// Flow Deliveries
const flowDeliveryRoute = createRoute({
  getParentRoute: () => flowRoute,
  path: '/deliveries/$deliveryId',
  component: lazyRouteComponent(() => import('_/components/Deliveries/SelectedDelivery')),
});

const flowDeliveryIndexRoute = createRoute({
  getParentRoute: () => flowDeliveryRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/orgs/$orgSlug/projects/$projectSlug/flows/$flowSlug/deliveries/$deliveryId/home',
      params: params,
    });
  },
});

const flowDeliveryHomeRoute = createRoute({
  getParentRoute: () => flowDeliveryRoute,
  path: '/home',
  component: lazyRouteComponent(() => import('_/components/Deliveries/DeliveryHome')),
});

const flowDeliveryAlarmsRoute = createRoute({
  getParentRoute: () => flowDeliveryRoute,
  path: '/alarms',
  component: lazyRouteComponent(() => import('_/components/Deliveries/DeliveryAlarms')),
});

const flowDeliveryDashboardRoute = createRoute({
  getParentRoute: () => flowDeliveryRoute,
  path: '/dashboard',
  component: lazyRouteComponent(() => import('_/components/Deliveries/DeliveryDashboard')),
});

const flowDeliveryNotificationsRoute = createRoute({
  getParentRoute: () => flowDeliveryRoute,
  path: '/notifications',
  component: lazyRouteComponent(() => import('_/components/Deliveries/DeliveryNotifications')),
});

const flowDeliveryPushesSpotlightRoute = createRoute({
  getParentRoute: () => flowDeliveryRoute,
  path: '/destinationpushesspotlight',
  component: lazyRouteComponent(() => import('_/components/Deliveries/DeliveryPushesSpotlight')),
});

const flowDeliverySnapshotSpotlightRoute = createRoute({
  getParentRoute: () => flowDeliveryRoute,
  path: '/snapshotspotlight',
  component: lazyRouteComponent(() => import('_/components/Deliveries/DeliverySnapshotSpotlight')),
});

const flowDeliverySnapshotsRoute = createRoute({
  getParentRoute: () => flowDeliveryRoute,
  path: '/snapshots',
  component: lazyRouteComponent(() => import('_/components/Deliveries/DeliverySnapshots')),
});

// Flow Alarm Groups
const flowAlarmGroupsRoute = createRoute({
  getParentRoute: () => flowRoute,
  path: '/alarmgroups',
  component: AlarmGroupsContainer,
});

const flowAlarmGroupsIndexRoute = createRoute({
  getParentRoute: () => flowAlarmGroupsRoute,
  path: '/',
  component: lazyRouteComponent(() => import('_/components/AlarmGroups/FlowAlarmGroups')),
});

const flowAlarmGroupRoute = createRoute({
  getParentRoute: () => flowAlarmGroupsRoute,
  path: '/$alarmGroupId',
  component: SelectedAlarmGroup,
});

const flowAlarmGroupIndexRoute = createRoute({
  getParentRoute: () => flowAlarmGroupRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/orgs/$orgSlug/projects/$projectSlug/flows/$flowSlug/alarmgroups/$alarmGroupId/home',
      params: params,
    });
  },
});

const flowAlarmGroupHomeRoute = createRoute({
  getParentRoute: () => flowAlarmGroupRoute,
  path: '/home',
  component: lazyRouteComponent(() => import('_/components/AlarmGroups/AlarmGroupHome')),
});

const flowAlarmGroupIntegrationsRoute = createRoute({
  getParentRoute: () => flowAlarmGroupRoute,
  path: '/integrations',
  component: lazyRouteComponent(() => import('_/components/AlarmGroups/AlarmGroupIntegrations')),
});

const flowAlarmGroupRunbookRoute = createRoute({
  getParentRoute: () => flowAlarmGroupRoute,
  path: '/runbook',
  component: lazyRouteComponent(() => import('_/components/AlarmGroups/AlarmGroupRunbook')),
});

// Flow Alarms
const flowAlarmsRoute = createRoute({
  getParentRoute: () => flowAlarmGroupRoute,
  path: '/alarms',
  component: AlarmsContainer,
});

const flowAlarmsIndexRoute = createRoute({
  getParentRoute: () => flowAlarmsRoute,
  path: '/',
  component: lazyRouteComponent(() => import('_/components/Alarms/AlarmGroupAlarms')),
});

const flowAlarmRoute = createRoute({
  getParentRoute: () => flowAlarmsRoute,
  path: '/$alarmId',
  component: SelectedAlarm,
});

const flowAlarmEditRoute = createRoute({
  getParentRoute: () => flowAlarmRoute,
  path: '/',
  component: lazyRouteComponent(() => import('_/components/Alarms/NewAlarm')),
});

// ============================================================================
// COLLECTION ROUTES
// ============================================================================

const collectionsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/collections',
  component: CollectionsContainer,
});

const collectionsIndexRoute = createRoute({
  getParentRoute: () => collectionsRoute,
  path: '/',
  component: lazyRouteComponent(() => import('_/components/Collections/ProjectCollections')),
});

const collectionsNewRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/collections/_new',
  component: lazyRouteComponent(() => import('_/components/Collections/NewCollection')),
});

const collectionRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/collections/$collectionSlug',
  component: lazyRouteComponent(() => import('_/components/Collections/SelectedCollection')),
});

const collectionIndexRoute = createRoute({
  getParentRoute: () => collectionRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/orgs/$orgSlug/projects/$projectSlug/collections/$collectionSlug/home',
      params: params,
    });
  },
});

const collectionHomeRoute = createRoute({
  getParentRoute: () => collectionRoute,
  path: '/home',
  component: lazyRouteComponent(() => import('_/components/Collections/CollectionHome')),
});

const collectionEditRoute = createRoute({
  getParentRoute: () => collectionRoute,
  path: '/edit',
  component: lazyRouteComponent(() => import('_/components/Collections/NewCollection')),
});

const collectionSettingsRoute = createRoute({
  getParentRoute: () => collectionRoute,
  path: '/settings',
  component: lazyRouteComponent(() => import('_/components/Collections/CollectionSettings')),
});

const collectionDestinationsRoute = createRoute({
  getParentRoute: () => collectionRoute,
  path: '/destinations',
  component: lazyRouteComponent(() => import('_/components/Collections/CollectionDestinations')),
});

const collectionSchemaRoute = createRoute({
  getParentRoute: () => collectionRoute,
  path: '/schema',
  component: lazyRouteComponent(() => import('_/components/Collections/CollectionSchema')),
});

const collectionPushesRoute = createRoute({
  getParentRoute: () => collectionRoute,
  path: '/pushes',
  component: CollectionPushes,
});

const collectionChecksRoute = createRoute({
  getParentRoute: () => collectionRoute,
  path: '/checks',
  component: lazyRouteComponent(() => import('_/components/Collections/CollectionChecks')),
});

const collectionSnapshotSpotlightRoute = createRoute({
  getParentRoute: () => collectionRoute,
  path: '/snapshotspotlight',
  component: lazyRouteComponent(() => import('_/components/Collections/CollectionSnapshotSpotlight')),
});

const collectionReadmeHistoryRoute = createRoute({
  getParentRoute: () => collectionRoute,
  path: '/readmeversions',
  component: lazyRouteComponent(() => import('_/components/Collections/CollectionReadmeHistory')),
});

// ============================================================================
// SOURCE ROUTES
// ============================================================================

const sourcesRoute = createRoute({
  getParentRoute: () => collectionRoute,
  path: '/sources',
  component: SourcesContainer,
});

const sourcesIndexRoute = createRoute({
  getParentRoute: () => sourcesRoute,
  path: '/',
  component: lazyRouteComponent(() => import('_/components/Sources/CollectionSources')),
});

const sourcesNewRoute = createRoute({
  getParentRoute: () => collectionRoute,
  path: '/sources/_new',
  component: lazyRouteComponent(() => import('_/components/Sources/NewSource')),
});

const sourceRoute = createRoute({
  getParentRoute: () => collectionRoute,
  path: '/sources/$sourceSlug',
  component: lazyRouteComponent(() => import('_/components/Sources/SelectedSource')),
});

const sourceIndexRoute = createRoute({
  getParentRoute: () => sourceRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/orgs/$orgSlug/projects/$projectSlug/collections/$collectionSlug/sources/$sourceSlug/home',
      params: params,
    });
  },
});

const sourceHomeRoute = createRoute({
  getParentRoute: () => sourceRoute,
  path: '/home',
  component: lazyRouteComponent(() => import('_/components/Sources/SourceHome')),
});

const sourceEditRoute = createRoute({
  getParentRoute: () => sourceRoute,
  path: '/edit',
  component: lazyRouteComponent(() => import('_/components/Sources/NewSource')),
});

const sourceSettingsRoute = createRoute({
  getParentRoute: () => sourceRoute,
  path: '/settings',
  component: SourceSettings,
});

const sourceGraphsRoute = createRoute({
  getParentRoute: () => sourceRoute,
  path: '/graphs',
  component: SourceGraphs,
});

const sourceReadmeHistoryRoute = createRoute({
  getParentRoute: () => sourceRoute,
  path: '/readmeversions',
  component: SourceReadmeHistory,
});

const sourceExtractorHistoryRoute = createRoute({
  getParentRoute: () => sourceRoute,
  path: '/extractorversions',
  component: lazyRouteComponent(() => import('_/components/Sources/SourceExtractorHistory')),
});

// ============================================================================
// SNAPSHOT ROUTES
// ============================================================================

const snapshotsRoute = createRoute({
  getParentRoute: () => sourceRoute,
  path: '/snapshots',
  component: SnapshotsContainer,
});

const snapshotsIndexRoute = createRoute({
  getParentRoute: () => snapshotsRoute,
  path: '/',
  component: lazyRouteComponent(() => import('_/components/Snapshots/SourceSnapshots')),
});

const snapshotRoute = createRoute({
  getParentRoute: () => snapshotsRoute,
  path: '/$snapshotId',
  component: lazyRouteComponent(() => import('_/components/Snapshots/SelectedSnapshot')),
});

const snapshotIndexRoute = createRoute({
  getParentRoute: () => snapshotRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/orgs/$orgSlug/projects/$projectSlug/collections/$collectionSlug/sources/$sourceSlug/snapshots/$snapshotId/home',
      params: params,
    });
  },
});

const snapshotHomeRoute = createRoute({
  getParentRoute: () => snapshotRoute,
  path: '/home',
  component: lazyRouteComponent(() => import('_/components/Snapshots/SnapshotHome')),
});

const snapshotDrilldownRoute = createRoute({
  getParentRoute: () => snapshotRoute,
  path: '/drilldown',
  component: SnapshotDrilldown,
});

const snapshotGraphsRoute = createRoute({
  getParentRoute: () => snapshotRoute,
  path: '/graphs',
  component: SnapshotGraphs,
});

const snapshotCompareRoute = createRoute({
  getParentRoute: () => snapshotRoute,
  path: '/compare',
  component: SnapshotCompare,
});

const snapshotChecksRoute = createRoute({
  getParentRoute: () => snapshotRoute,
  path: '/tests',
  component: lazyRouteComponent(() => import('_/components/Snapshots/SnapshotChecks')),
});

const snapshotChunksRoute = createRoute({
  getParentRoute: () => snapshotRoute,
  path: '/chunks',
  component: lazyRouteComponent(() => import('_/components/Snapshots/SnapshotChunks')),
});

const snapshotImportingRoute = createRoute({
  getParentRoute: () => snapshotRoute,
  path: '/importing',
  component: lazyRouteComponent(() => import('_/components/Snapshots/ImportStatus')),
});

// ============================================================================
// LOGIN POOL & LOGIN ROUTES
// ============================================================================

const loginPoolsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/loginpools',
  component: LoginPoolsContainer,
});

const loginPoolsIndexRoute = createRoute({
  getParentRoute: () => loginPoolsRoute,
  path: '/',
  component: lazyRouteComponent(() => import('_/components/LoginPools/LoginPools')),
});

const loginPoolsNewRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/loginpools/_new',
  component: lazyRouteComponent(() => import('_/components/LoginPools/EditLoginPool/EditLoginPool')),
});

const loginPoolRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/loginpools/$loginPoolSlug',
  component: SelectedLoginPool,
});

const loginPoolEditRoute = createRoute({
  getParentRoute: () => loginPoolRoute,
  path: '/',
  component: lazyRouteComponent(() => import('_/components/LoginPools/EditLoginPool/EditLoginPool')),
});

const loginsRoute = createRoute({
  getParentRoute: () => loginPoolRoute,
  path: '/logins',
  component: LoginsContainer,
});

const loginsIndexRoute = createRoute({
  getParentRoute: () => loginsRoute,
  path: '/',
  component: lazyRouteComponent(() => import('_/components/Logins/Logins')),
});

const loginsNewRoute = createRoute({
  getParentRoute: () => loginPoolRoute,
  path: '/logins/_new',
  component: lazyRouteComponent(() => import('_/components/Logins/EditLogin/EditLogin')),
});

const loginRoute = createRoute({
  getParentRoute: () => loginPoolRoute,
  path: '/logins/$loginId',
  component: SelectedLogin,
});

const loginEditRoute = createRoute({
  getParentRoute: () => loginRoute,
  path: '/',
  component: lazyRouteComponent(() => import('_/components/Logins/EditLogin/EditLogin')),
});

// ============================================================================
// ROUTE TREE
// ============================================================================

const routeTree = rootRoute.addChildren([
  apiDocsRoute,
  docsRoute,
  userLoginRoute,
  appRoute.addChildren([
    indexRoute,
    orgsIndexRoute,
    systemUsersRoute,
    userAccountRoute,
    recentSnapshotsRoute,
    extractionCellsRoute.addChildren([extractionCellsIndexRoute, extractionCellsNewRoute, extractionCellsEditRoute]),
    forbiddenRoute,
    notFoundRoute,
    orgRoute.addChildren([
      orgIndexRoute,
      orgHomeRoute,
      orgReadmeHistoryRoute,
      orgSettingsRoute,
      orgPeopleRoute,
      orgIntegrationsRoute,
      selectedIntegrationRoute.addChildren([editIntegrationRoute]),
      orgDeliveriesRoute.addChildren([
        orgDeliveriesIndexRoute,
        orgDeliveryRoute.addChildren([orgDeliveryIndexRoute, orgDeliveryHomeRoute]),
      ]),
      destinationsRoute.addChildren([destinationsIndexRoute, newDestinationRoute, editDestinationRoute]),
      schemasRoute.addChildren([schemasIndexRoute, schemaNewRoute, schemaEditRoute, schemaRoute]),
      extractorsRoute.addChildren([extractorsIndexRoute]),
      snapshotShortcutRoute,
      projectsRoute,
      projectsNewRoute,
      projectRoute.addChildren([
        projectIndexRoute,
        projectHomeRoute,
        projectEditRoute,
        projectReadmeHistoryRoute,
        projectCellRoutesRoute,
        projectCellMetricsRoute,
        projectQaQueueRoute,
        projectFixQueueRoute,
        projectMyQARoute,
        projectMyFixingRoute,
        flowsRoute,
        flowsNewRoute,
        flowRoute.addChildren([
          flowIndexRoute,
          flowHomeRoute,
          flowEditRoute,
          flowReadmeHistoryRoute,
          flowDeliveriesRoute,
          flowDestinationsRoute,
          flowSettingsRoute,
          flowSegmentSettingsRoute,
          flowRunRoute,
          flowDeliveryRoute.addChildren([
            flowDeliveryIndexRoute,
            flowDeliveryHomeRoute,
            flowDeliveryAlarmsRoute,
            flowDeliveryDashboardRoute,
            flowDeliveryNotificationsRoute,
            flowDeliveryPushesSpotlightRoute,
            flowDeliverySnapshotSpotlightRoute,
            flowDeliverySnapshotsRoute,
          ]),
          flowAlarmGroupsRoute.addChildren([
            flowAlarmGroupsIndexRoute,
            flowAlarmGroupRoute.addChildren([
              flowAlarmGroupIndexRoute,
              flowAlarmGroupHomeRoute,
              flowAlarmGroupIntegrationsRoute,
              flowAlarmGroupRunbookRoute,
              flowAlarmsRoute.addChildren([flowAlarmsIndexRoute, flowAlarmRoute.addChildren([flowAlarmEditRoute])]),
            ]),
          ]),
        ]),
        collectionsRoute.addChildren([collectionsIndexRoute]),
        collectionsNewRoute,
        collectionRoute.addChildren([
          collectionIndexRoute,
          collectionHomeRoute,
          collectionEditRoute,
          collectionSettingsRoute,
          collectionDestinationsRoute,
          collectionSchemaRoute,
          collectionPushesRoute,
          collectionChecksRoute,
          collectionSnapshotSpotlightRoute,
          collectionReadmeHistoryRoute,
          sourcesRoute.addChildren([sourcesIndexRoute]),
          sourcesNewRoute,
          sourceRoute.addChildren([
            sourceIndexRoute,
            sourceHomeRoute,
            sourceEditRoute,
            sourceSettingsRoute,
            sourceGraphsRoute,
            sourceReadmeHistoryRoute,
            sourceExtractorHistoryRoute,
            snapshotsRoute.addChildren([
              snapshotsIndexRoute,
              snapshotRoute.addChildren([
                snapshotIndexRoute,
                snapshotHomeRoute,
                snapshotDrilldownRoute,
                snapshotGraphsRoute,
                snapshotCompareRoute,
                snapshotChecksRoute,
                snapshotChunksRoute,
                snapshotImportingRoute,
              ]),
            ]),
          ]),
        ]),
        loginPoolsRoute.addChildren([loginPoolsIndexRoute]),
        loginPoolsNewRoute,
        loginPoolRoute.addChildren([
          loginPoolEditRoute,
          loginsRoute.addChildren([loginsIndexRoute]),
          loginsNewRoute,
          loginRoute.addChildren([loginEditRoute]),
        ]),
      ]),
    ]),
  ]),
]);

// ============================================================================
// ROUTER INSTANCE
// ============================================================================

export const router = createRouter({
  routeTree: routeTree,
  defaultPreload: 'intent',
  defaultNotFoundComponent: NotFound,
});

// Register router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
